import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "@/lib/router-compat";
import { translations, type Lang } from "./translations";
import { getSupabase } from "@/lib/supabase-lazy";
import { useAuthState } from "@/hooks/useAuthState";

interface LocalizedPaths {
  pathEs: string;
  pathEn: string;
}

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (section: string, key: string) => string;
  tArr: (section: string, key: string) => string[];
  tObj: (section: string, key: string) => any;
  /** Dynamic per-page overrides (e.g. blog articles whose slug differs ES/EN). */
  localizedPaths: LocalizedPaths | null;
  setLocalizedPaths: (paths: LocalizedPaths | null) => void;
}

const LanguageContext = createContext<LanguageContextType | null>(null);
const LANGUAGE_SYNC_TIMEOUT_MS = 3_500;

const withLanguageTimeout = async <T,>(promise: PromiseLike<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("language_sync_timeout")), LANGUAGE_SYNC_TIMEOUT_MS);
  });

  try {
    return await Promise.race([Promise.resolve(promise), timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const detectLang = (): Lang => {
  // Client-only: refine language after hydration.
  if (typeof window === "undefined") return "es";
  // URL is the strict source of truth on bootstrap.
  if (window.location.pathname.startsWith("/en")) {
    return "en";
  }
  const saved = localStorage.getItem("lang");
  if (saved === "en" || saved === "es") return saved;
  const browserLang = navigator.language.slice(0, 2);
  return browserLang === "es" ? "es" : "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  // The initial render must be identical on server and client: derive language
  // strictly from the URL. localStorage/navigator detection runs post-hydration.
  const [lang, setLangState] = useState<Lang>(location.pathname.startsWith("/en") ? "en" : "es");
  const [localizedPaths, setLocalizedPaths] = useState<LocalizedPaths | null>(null);
  const { isReady, isAuthenticated, user } = useAuthState();
  const hydratedLanguageUserRef = useRef<string | null>(null);
  const lastPersistedLanguageRef = useRef<string>("");

  // Solo sincroniza og:locale con el idioma activo. El title, la description y
  // el resto de etiquetas OG/Twitter son propiedad de cada página (`head()` de
  // la ruta + `<SEO />`); sobrescribirlas aquí generaba metadatos genéricos
  // duplicados y títulos incorrectos por página.
  const updateMetaTags = useCallback((l: Lang) => {
    const locale = l === "es" ? "es_ES" : "en_US";
    const el = document.querySelector("meta[property='og:locale']");
    if (el) el.setAttribute("content", locale);
  }, []);


  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem("lang", l);
    document.documentElement.lang = l;
    updateMetaTags(l);
  }, [updateMetaTags]);

  // Re-detect the language on the client after hydration (SSR always renders "es").
  useEffect(() => {
    const detected = detectLang();
    setLangState((current) => (current === detected ? current : detected));
    document.documentElement.lang = detected;
  }, []);



  // Sync UI language from the profile after auth is ready. Keep this out of
  // onAuthStateChange: awaiting backend reads inside that callback can hold the
  // auth lock and leave /dashboard stuck in its loading shell after Google OAuth.
  useEffect(() => {
    if (!isReady || !isAuthenticated || !user?.id) {
      hydratedLanguageUserRef.current = null;
      return;
    }

    let cancelled = false;
    const uid = user.id;
    hydratedLanguageUserRef.current = null;

    void (async () => {
      try {
        const supabase = await getSupabase();
        const { data } = await withLanguageTimeout(
          supabase
            .from("profiles")
            .select("preferred_language")
            .eq("user_id", uid)
            .maybeSingle(),
        );
        if (cancelled) return;
        const pl = (data as any)?.preferred_language;
        if (pl === "es" || pl === "en") {
          setLangState(pl);
          localStorage.setItem("lang", pl);
          document.documentElement.lang = pl;
          updateMetaTags(pl);
          lastPersistedLanguageRef.current = `${uid}:${pl}`;
        }
      } catch { /* ignore */ }
      finally {
        if (!cancelled) hydratedLanguageUserRef.current = uid;
      }
    })();

    return () => { cancelled = true; };
  }, [isReady, isAuthenticated, user?.id, updateMetaTags]);

  // Persist explicit language changes once the initial profile language read has
  // completed. This avoids overwriting a user's saved preference during OAuth boot.
  useEffect(() => {
    if (!isReady || !isAuthenticated || !user?.id) return;
    if (hydratedLanguageUserRef.current !== user.id) return;
    const key = `${user.id}:${lang}`;
    if (lastPersistedLanguageRef.current === key) return;
    lastPersistedLanguageRef.current = key;

    void (async () => {
      try {
        const supabase = await getSupabase();
        await withLanguageTimeout(
          supabase
            .from("profiles")
            .update({ preferred_language: lang })
            .eq("user_id", user.id),
        );
      } catch { /* ignore */ }
    })();
  }, [isReady, isAuthenticated, user?.id, lang]);


  const t = useCallback((section: string, key: string): string => {
    const sec = (translations as any)[section];
    if (!sec) return `[${section}.${key}]`;
    const entry = sec[key];
    if (!entry) return `[${section}.${key}]`;
    return entry[lang] ?? entry["es"] ?? `[${section}.${key}]`;
  }, [lang]);

  const tArr = useCallback((section: string, key: string): string[] => {
    const sec = (translations as any)[section];
    if (!sec) return [];
    const entry = sec[key];
    if (!entry) return [];
    return entry[lang] ?? entry["es"] ?? [];
  }, [lang]);

  const tObj = useCallback((section: string, key: string): any => {
    const sec = (translations as any)[section];
    if (!sec) return null;
    const entry = sec[key];
    if (!entry) return null;
    return entry[lang] ?? entry["es"] ?? null;
  }, [lang]);

  // Mantiene <html lang> y las meta OG sincronizadas con el idioma activo,
  // tanto en el arranque como al cambiarlo desde el selector de la navbar.
  useEffect(() => {
    updateMetaTags(lang);
    document.documentElement.lang = lang;
  }, [lang, updateMetaTags]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tArr, tObj, localizedPaths, setLocalizedPaths }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};

/**
 * Fuerza español dentro del back office sin tocar el idioma guardado
 * (localStorage, perfil): solo sobrescribe el contexto para sus hijos.
 */
export const AdminSpanishScope = ({ children }: { children: ReactNode }) => {
  const parent = useLanguage();
  const pick = (section: string, key: string) => (translations as any)[section]?.[key]?.es;
  const value: LanguageContextType = {
    ...parent,
    lang: "es",
    t: (s, k) => pick(s, k) ?? `[${s}.${k}]`,
    tArr: (s, k) => pick(s, k) ?? [],
    tObj: (s, k) => pick(s, k) ?? null,
  };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
